// src/components/modals/ConfirmDialog.tsx
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useResponsive } from "@h/useResponsive";
import { cn } from "@u/cn";
import Button from "@ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@ui/dialog";
import ResponsiveSheet from "@ui/responsive-sheet";
import {
  AlertTriangle,
  HelpCircle,
  SaveAll,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * Canonical confirmation dialog. One variant-driven composition for every
 * "user is about to do something sensitive — yes/no" moment. Replaces
 * ad-hoc DeleteModal / UnsavedChangesModal / WarningModal / etc.
 *
 * Prefer the imperative helper {@link confirmDialog} (returns Promise<boolean>)
 * over showing this directly. Variant drives icon + tone + confirm-button
 * design + initial focus + default copy. See CLAUDE.md §13.
 *
 * NOT for: success/empty/error states (use FeedbackState), transient
 * confirmations (Sonner toast), or non-blocking choices.
 */
export type ConfirmVariant =
  | "default"
  | "destructive"
  | "warning"
  | "unsaved";

interface VariantConfig {
  icon: LucideIcon;
  /** Icon-circle tone classes — token-based, no hex (CLAUDE.md §13). */
  tone: string;
  confirmDesign: "default" | "destructive";
  /** Default i18n key for the confirm label. */
  confirmKey: string;
  /** Error-prevention: destructive/warning focus Cancel, not Confirm. */
  focusCancel: boolean;
  alert: boolean;
}

const VARIANTS: Record<ConfirmVariant, VariantConfig> = {
  default: {
    icon: HelpCircle,
    tone: "bg-primary/10 text-primary",
    confirmDesign: "default",
    confirmKey: "confirm",
    focusCancel: false,
    alert: false,
  },
  destructive: {
    icon: Trash2,
    tone: "bg-destructive/10 text-destructive",
    confirmDesign: "destructive",
    confirmKey: "confirm_delete",
    focusCancel: true,
    alert: true,
  },
  warning: {
    icon: AlertTriangle,
    tone: "bg-warning/10 text-warning",
    confirmDesign: "default",
    confirmKey: "confirm",
    focusCancel: true,
    alert: true,
  },
  unsaved: {
    icon: SaveAll,
    tone: "bg-primary/10 text-primary",
    confirmDesign: "default",
    confirmKey: "save_and_exit",
    focusCancel: false,
    alert: false,
  },
};

export interface ConfirmDialogProps {
  variant?: ConfirmVariant;
  /** Heading. Defaults to a generic "Are you sure?" when omitted. */
  title?: string;
  description?: ReactNode;
  /**
   * Convenience for destructive deletes: when no `description` is given, renders
   * the standard "delete X — can't be undone" copy with the name emphasized.
   */
  itemName?: string;
  icon?: LucideIcon;
  confirmLabel?: string;
  cancelLabel?: string;
  /** destructive only: require typing this exact string to enable confirm. */
  confirmPhrase?: string;
  /** Primary action. May be async — confirm button shows loading while it runs. */
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  /** unsaved variant: middle "discard" action. */
  onDiscard?: () => void;
  /** Force loading from outside (legacy isActionPending). */
  loading?: boolean;
  /** Internal: resolves the confirmDialog() promise. */
  onResult?: (confirmed: boolean) => void;
}

function ConfirmDialogInner({
  variant = "default",
  title,
  description,
  itemName,
  icon,
  confirmLabel,
  cancelLabel,
  confirmPhrase,
  onConfirm,
  onCancel,
  onDiscard,
  loading,
  onResult,
}: ConfirmDialogProps) {
  const modal = useModal();
  const { t } = useTranslation();
  const { isMobileOrLandscape } = useResponsive();
  const descId = useId();

  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const cfg = VARIANTS[variant];
  const Icon = icon ?? cfg.icon;
  const busy = pending || !!loading;

  // Destructive deletes with only an itemName get the standard emphasized copy.
  const effectiveDescription: ReactNode =
    description ??
    (variant === "destructive" && itemName ? (
      <Trans
        i18nKey="delete_confirmation"
        values={{ itemName }}
        components={{
          1: <span className="text-destructive font-black uppercase" />,
        }}
      />
    ) : undefined);

  // type-to-confirm gate (destructive only)
  const [phrase, setPhrase] = useState("");
  const phraseLocked = !!confirmPhrase && phrase.trim() !== confirmPhrase;

  // Initial focus: Cancel for destructive/warning (prevention), else Confirm.
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!modal.visible) return;
    const target = cfg.focusCancel ? cancelRef.current : confirmRef.current;
    const id = window.setTimeout(() => target?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [modal.visible, cfg.focusCancel]);

  const finish = (confirmed: boolean) => {
    onResult?.(confirmed);
    modal.hide();
  };

  const handleConfirm = async () => {
    if (phraseLocked || busy) return;
    setError(false);
    setPending(true);
    try {
      await onConfirm?.();
      finish(true);
    } catch (err) {
      console.error("ConfirmDialog: onConfirm failed", err);
      setError(true);
    } finally {
      setPending(false);
    }
  };

  const handleCancel = () => {
    if (busy) return; // don't abort mid-action
    onCancel?.();
    finish(false);
  };

  const handleDiscard = () => {
    if (busy) return;
    onDiscard?.();
    finish(false);
  };

  // ESC / overlay click → cancel (blocked while busy).
  const handleOpenChange = (open: boolean) => {
    if (open) return;
    handleCancel();
  };

  const actions =
    variant === "unsaved" ? (
      <div className="flex flex-col gap-3 w-full">
        <Button
          ref={confirmRef}
          design="default"
          label={confirmLabel ?? t(cfg.confirmKey)}
          loading={busy}
          onClick={handleConfirm}
          className="w-full"
        />
        <div className="flex flex-wrap gap-3 w-full">
          <Button
            design="outline"
            label={t("discard_changes")}
            disabled={busy}
            onClick={handleDiscard}
            className="w-1/2"
          />
          <Button
            ref={cancelRef}
            design="outline"
            label={cancelLabel ?? t("continue_editing")}
            disabled={busy}
            onClick={handleCancel}
            className="w-1/2"
          />
        </div>
      </div>
    ) : (
      <div className="flex flex-col-reverse sm:flex-row gap-3 w-full sm:justify-end">
        <Button
          ref={cancelRef}
          design="outline"
          label={cancelLabel ?? t("cancel")}
          disabled={busy}
          onClick={handleCancel}
          className="w-full sm:w-auto"
        />
        <Button
          ref={confirmRef}
          design={cfg.confirmDesign}
          icon={variant === "destructive" ? Trash2 : undefined}
          label={confirmLabel ?? t(cfg.confirmKey)}
          loading={busy}
          disabled={phraseLocked}
          onClick={handleConfirm}
          className="w-full sm:w-auto"
        />
      </div>
    );

  const body = (
    <div className="flex flex-col items-center text-center gap-6 py-4">
      <div
        aria-hidden
        className={cn(
          "flex h-20 w-20 items-center justify-center rounded-3xl shadow-inner shrink-0 animate-in zoom-in duration-500",
          cfg.tone,
        )}
      >
        <Icon className="w-8 h-8" strokeWidth={1.5} />
      </div>

      <div className="space-y-3">
        <h3 className="text-xl font-medium">{title}</h3>
        {effectiveDescription && (
          <p id={descId} className="text-body text-xs mx-auto max-w-xs">
            {effectiveDescription}
          </p>
        )}
        {error && (
          <p role="alert" className="text-xs text-destructive font-medium">
            {t("error_generic")}
          </p>
        )}
      </div>

      {confirmPhrase && variant === "destructive" && (
        <input
          autoFocus={false}
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          placeholder={confirmPhrase}
          className="w-full rounded-lg border border-border/60 bg-transparent px-4 h-11 text-center text-base md:text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-destructive/40"
          aria-label={confirmPhrase}
        />
      )}

      {actions}
    </div>
  );

  if (isMobileOrLandscape) {
    return (
      <ResponsiveSheet
        open={modal.visible}
        onOpenChange={handleOpenChange}
        title={title}
        variant="default"
      >
        {body}
      </ResponsiveSheet>
    );
  }

  return (
    <Dialog open={modal.visible} onOpenChange={handleOpenChange}>
      <DialogContent
        size="sm"
        role={cfg.alert ? "alertdialog" : "dialog"}
        aria-describedby={effectiveDescription ? descId : undefined}
        className="w-[92vw] max-w-md p-6 sm:p-10 sm:rounded-[3rem]"
      >
        <DialogHeader>
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogDescription className="sr-only">
            {typeof effectiveDescription === "string"
              ? effectiveDescription
              : title}
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

const ConfirmDialog = NiceModal.create(ConfirmDialogInner);
export default ConfirmDialog;
export { ConfirmDialogInner };

/**
 * Imperative confirm. Resolves true if confirmed, false if cancelled/dismissed.
 * The async `onConfirm` runs inside the dialog so the confirm button shows
 * loading; if it throws, the dialog stays open with an error and the promise
 * does NOT resolve until the user retries or cancels.
 *
 * @example
 * const ok = await confirmDialog({
 *   variant: "destructive",
 *   title: t("delete_project"),
 *   description: t("delete_project_desc"),
 *   onConfirm: () => deleteProject(id),
 * });
 * if (!ok) return;
 */
// eslint-disable-next-line react-refresh/only-export-components -- imperative helper colocated with its dialog component
export function confirmDialog(
  opts: Omit<ConfirmDialogProps, "onResult">,
): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (v: boolean) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    NiceModal.show(ConfirmDialog, {
      ...opts,
      onResult: settle,
    } satisfies ConfirmDialogProps);
  });
}
